import React, { useCallback, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { fmtAlbaranNro } from "@/lib/textos";

function compareValues(a, b) {
  const empty = (v) => v == null || v === "";
  if (empty(a) && empty(b)) return 0;
  if (empty(a)) return 1;
  if (empty(b)) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  const na = typeof a === "string" && a.trim() !== "" && !Number.isNaN(Number(a)) ? Number(a) : NaN;
  const nb = typeof b === "string" && b.trim() !== "" && !Number.isNaN(Number(b)) ? Number(b) : NaN;
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  return String(a).localeCompare(String(b), "es", { numeric: true, sensitivity: "base" });
}

export function useTableSort(rows, getters) {
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const toggle = useCallback((key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  }, []);
  const sorted = useMemo(() => {
    if (!sort.key || !getters?.[sort.key]) return rows;
    const get = getters[sort.key];
    const mul = sort.dir === "desc" ? -1 : 1;
    return [...rows].sort((a, b) => mul * compareValues(get(a), get(b)));
  }, [rows, sort, getters]);
  return { sorted, sort, toggle };
}

export function SortableTh({ label, sortKey, sort, onSort, className, align = "left" }) {
  const active = sort?.key === sortKey;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      className={cn(align === "right" && "text-right", align === "center" && "text-center", className)}
      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        data-testid={`sort-${sortKey}`}
        aria-label={active
          ? `Ordenar por ${label}, ${sort.dir === "asc" ? "ascendente" : "descendente"}`
          : `Ordenar por ${label}`}
        className={cn(
          "inline-flex items-center gap-1 w-full text-[11px] tracking-[0.06em] uppercase font-semibold text-slate-500 hover:text-slate-800",
          align === "right" && "justify-end",
          align === "center" && "justify-center",
          active && "text-slate-800",
        )}
      >
        <span>{label}</span>
        <Icon className={cn("h-3 w-3 shrink-0", active ? "text-blue-700" : "text-slate-400")} strokeWidth={2} />
      </button>
    </th>
  );
}

export function claveEnvioAlb(e) {
  return `${e.albaran_ejercicio || 0}|${e.albaran_serie || ""}|${e.albaran_numero || 0}`;
}

const ENVIOS_GETTERS = {
  orden: (e) => e.orden,
  origen: (e) => e.origen || "",
  destino: (e) => `${e.destinatario_nombre || ""} ${e.destino || ""}`,
  naturaleza: (e) => e.naturaleza || "",
  bultos: (e) => Number(e.bultos) || 0,
  peso: (e) => Number(e.peso_bruto) || 0,
  albaran: (e) => fmtAlbaranNro(e.albaran_serie, e.albaran_numero),
};

export function EnviosSortableTable({
  envios,
  rowKey = (e) => e.id || e.orden,
  destinoLabel = "Destino",
  className = "deca-table w-full",
  onEditEnvio,
  editValues,
  renderDestino,
  renderOrigen,
}) {
  const { sorted, sort, toggle } = useTableSort(envios || [], ENVIOS_GETTERS);
  const editable = typeof onEditEnvio === "function";
  const valor = (e, field) => {
    const ov = editValues?.[claveEnvioAlb(e)];
    if (ov && ov[field] !== undefined && ov[field] !== "") return ov[field];
    return e[field];
  };
  return (
    <table className={className}>
      <thead>
        <tr>
          <SortableTh label="#" sortKey="orden" sort={sort} onSort={toggle} />
          <SortableTh label="Origen" sortKey="origen" sort={sort} onSort={toggle} />
          <SortableTh label={destinoLabel} sortKey="destino" sort={sort} onSort={toggle} />
          <SortableTh label="Naturaleza" sortKey="naturaleza" sort={sort} onSort={toggle} />
          <SortableTh label="Bultos" sortKey="bultos" sort={sort} onSort={toggle} align="right" />
          <SortableTh label="Peso" sortKey="peso" sort={sort} onSort={toggle} align="right" />
          <SortableTh label={"Albar" + String.fromCharCode(0xe1) + "n"} sortKey="albaran" sort={sort} onSort={toggle} />
        </tr>
      </thead>
      <tbody>
        {sorted.map((e) => (
          <tr key={rowKey(e)}>
            <td className="font-mono">{e.orden}</td>
            <td>{typeof renderOrigen === "function" ? renderOrigen(e) : e.origen}</td>
            <td>
              {typeof renderDestino === "function" ? renderDestino(e) : (
                <>
                  <div className="font-medium">{e.destinatario_nombre}</div>
                  <div className="text-xs text-slate-500">{e.destino}</div>
                </>
              )}
            </td>
            <td className="max-w-[280px]"><div className="line-clamp-2">{e.naturaleza}</div></td>
            <td className="text-right font-mono">
              {editable ? (
                <input
                  type="number"
                  min="0"
                  step="1"
                  className="w-20 ml-auto block rounded-md border border-slate-200 px-2 py-1 text-right text-sm"
                  value={valor(e, "bultos") ?? ""}
                  onChange={(ev) => onEditEnvio(e, { bultos: ev.target.value })}
                  data-testid={`envio-bultos-${claveEnvioAlb(e)}`}
                />
              ) : (e.bultos ?? "\u2014")}
            </td>
            <td className="text-right font-mono">
              {editable ? (
                <div className="flex items-center justify-end gap-1">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-24 rounded-md border border-slate-200 px-2 py-1 text-right text-sm"
                    value={valor(e, "peso_bruto") ?? ""}
                    onChange={(ev) => onEditEnvio(e, { peso_bruto: ev.target.value })}
                    data-testid={`envio-peso-${claveEnvioAlb(e)}`}
                  />
                  <span className="text-xs text-slate-500">kg</span>
                </div>
              ) : `${e.peso_bruto} kg`}
            </td>
            <td className="font-mono text-xs">{fmtAlbaranNro(e.albaran_serie, e.albaran_numero)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
